import { z } from 'zod';

/**
 * POST {auth}/api/pos/auth/login — the ONLY auth route that exists
 * (auth/routes/api.php:3-7). No me/logout/refresh.
 *
 * Verified response shape (auth/app/Http/Controllers/Api/POS/AuthController.php:19-36):
 * - top-level `organizations` is a normalized map of
 *   {id, name, domain, status, role}; it omits `type`.
 * - `user` is the raw Eloquent user WITH the eager-loaded `organizations`
 *   relation, i.e. FULL central Organization models including `type`, `status`
 *   and pivot (auth/app/Models/Organization.php:15-26 casts type to the
 *   restaurant|ecommerce|quotation enum). This is the only place the login
 *   payload carries the organization type — the adapter reads it from here
 *   and NEVER guesses when absent.
 */

export const organizationTypeSchema = z.enum(['restaurant', 'ecommerce', 'quotation']);
export type OrganizationType = z.infer<typeof organizationTypeSchema>;

/**
 * Per-organization membership role (V1-ROLES / blocker B8). Added ADDITIVELY by
 * auth on both the top-level `organizations[]` rows and
 * `user.organizations[].pivot.role`. Absent = the backend did not say; the
 * console then makes NO capability assumption and shows the full navigation,
 * exactly as before roles existed (07-roles-permissions.md §4).
 *
 * UX ONLY. pos-api's PosRoleMiddleware is authoritative; hiding a control here
 * is a convenience, never a security boundary.
 */
export const organizationRoleSchema = z.enum(['owner', 'manager', 'cashier', 'waiter']);
export type OrganizationRole = z.infer<typeof organizationRoleSchema>;

export const salesControlsSchema = z.object({
  pos_enabled: z.boolean(),
  online_store_enabled: z.boolean(),
  cash_reconciliation_enabled: z.boolean(),
  staff_limit: z.number().nullable(),
  location_limit: z.number().nullable(),
  terminal_limit: z.number().nullable(),
});
export type SalesControls = z.infer<typeof salesControlsSchema>;

/** Top-level login `organizations` rows. */
export const wireOrganizationLiteSchema = z.looseObject({
  sales_controls: salesControlsSchema.optional(),
  id: z.number(),
  name: z.string(),
  domain: z.string(),
  status: z.string().nullish(),
  /** Added additively by V1-ROLES; absent on pre-roles deployments. */
  role: z.string().nullish(),
});
export type WireOrganizationLite = z.infer<typeof wireOrganizationLiteSchema>;

/** user.organizations rows: full central Organization model (may carry `type`). */
export const wireUserOrganizationSchema = z.looseObject({
  id: z.number(),
  name: z.string(),
  domain: z.string().nullish(),
  /** restaurant | ecommerce | quotation — validated by the adapter, unknown values kept as-is here. */
  type: z.string().nullish(),
  status: z.string().nullish(),
  /** withPivot('role') — the same value the top-level row carries. */
  pivot: z.looseObject({ role: z.string().nullish() }).nullish(),
});
export type WireUserOrganization = z.infer<typeof wireUserOrganizationSchema>;

export const wireAuthUserSchema = z.looseObject({
  id: z.number(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  email: z.string(),
  organizations: z.array(wireUserOrganizationSchema).optional(),
});
export type WireAuthUser = z.infer<typeof wireAuthUserSchema>;

export const wireLoginResponseSchema = z.looseObject({
  access_token: z.string().min(1),
  token_type: z.string().optional(),
  user: wireAuthUserSchema,
  organizations: z.array(wireOrganizationLiteSchema).optional().default([]),
});
export type WireLoginResponse = z.infer<typeof wireLoginResponseSchema>;

// ---------------------------------------------------------------------------
// Normalized domain types
// ---------------------------------------------------------------------------

export interface Organization {
  salesControls?: SalesControls;
  id: number;
  name: string;
  domain: string;
  /**
   * organizations.type when the login payload carried a valid value;
   * null = the backend did not say. Consumers must treat null as a neutral
   * mode — NEVER guess restaurant/ecommerce from other data.
   */
  type: OrganizationType | null;
  /**
   * Membership role, or null when the backend did not report one (pre-roles
   * deployment). Null means "assume nothing" — see organizationRoleSchema.
   */
  role: OrganizationRole | null;
  /** Central lifecycle state. Suspended stores remain selectable so the UI can explain why access is blocked. */
  status?: 'active' | 'suspended' | null;
}

export interface AuthUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

export interface LoginResult {
  token: string;
  user: AuthUser;
  organizations: Organization[];
}

/**
 * Merge the top-level lite list with per-user organization rows so `type`
 * survives when present. Membership comes from the top-level list (both are
 * derived from the same relation server-side); type/domain fall back to the
 * user.organizations row with the same id.
 */
export function normalizeLoginResponse(wire: WireLoginResponse): LoginResult {
  const detailById = new Map<number, WireUserOrganization>();
  for (const org of wire.user.organizations ?? []) detailById.set(org.id, org);

  const lite = wire.organizations.length
    ? wire.organizations
    : // Defensive: if the top-level list ever goes missing, fall back to the
      // eager-loaded relation rather than locking the user out.
      (wire.user.organizations ?? []).map((o) => ({
        id: o.id,
        name: o.name,
        domain: o.domain ?? '',
        status: o.status,
        role: o.pivot?.role ?? null,
        sales_controls: salesControlsSchema.safeParse(o).data,
      }));

  const organizations: Organization[] = lite.map((org) => {
    const detail = detailById.get(org.id);
    const parsedType = organizationTypeSchema.safeParse(detail?.type);
    // Role may arrive on the top-level row or the eager-loaded pivot; either
    // is authoritative, an unknown value is treated as absent.
    const parsedRole = organizationRoleSchema.safeParse(org.role ?? detail?.pivot?.role);
    return {
      id: org.id,
      name: org.name,
      domain: org.domain,
      type: parsedType.success ? parsedType.data : null,
      role: parsedRole.success ? parsedRole.data : null,
      salesControls: salesControlsSchema.safeParse(org.sales_controls).success
        ? salesControlsSchema.parse(org.sales_controls) : undefined,
      status: org.status === 'active' || org.status === 'suspended' ? org.status : null,
    };
  });

  return {
    token: wire.access_token,
    user: {
      id: wire.user.id,
      first_name: wire.user.first_name ?? '',
      last_name: wire.user.last_name ?? '',
      email: wire.user.email,
    },
    organizations,
  };
}

// ---------------------------------------------------------------------------
// GET {pos}/me — the caller's EFFECTIVE context (V2-1 staleness fix)
// ---------------------------------------------------------------------------

/**
 * The login payload above is a SNAPSHOT: `auth` has one route and no `/me`, so
 * everything the console learned about the user, the organization's `type` and
 * the membership `role` was frozen at sign-in and could only be corrected by
 * signing out and back in. pos-api now answers the same question on demand
 * (pos-api routes/api.php, `GET pos/me` → Api\POS\MeController), because it is
 * the layer that already resolves the organization and enforces the role on
 * every request.
 *
 * This is a CORRECTION channel, not a second login. It never produces a token
 * and it never signs anyone out; see AuthStore.reconcile().
 */
export const wireMeOrganizationSchema = z.looseObject({
  sales_controls: salesControlsSchema.optional(),
  id: z.number(),
  name: z.string(),
  domain: z.string().nullish(),
  type: z.string().nullish(),
  role: z.string().nullish(),
});

export const wireMeResponseSchema = z.looseObject({
  data: z.looseObject({
    user: z.looseObject({
      id: z.number(),
      first_name: z.string().nullish(),
      last_name: z.string().nullish(),
      email: z.string(),
    }),
    organization: wireMeOrganizationSchema.nullish(),
    organizations: z.array(wireMeOrganizationSchema).default([]),
    /**
     * Resolved for THIS role by the server. Kept as plain strings: the console's
     * src/lib/capabilities.ts owns the vocabulary, and an unknown capability
     * name from a newer backend must not make the whole payload unparseable.
     */
    capabilities: z.array(z.string()).default([]),
    /**
     * Every capability name the BACKEND knows, granted or not. Without it a
     * name missing from `capabilities` is ambiguous — "this role may not" and
     * "this backend has not heard of it" look identical and need opposite
     * handling. Defaults to empty, which lib/capabilities.ts reads as "no
     * server opinion" and falls back to its own table for everything.
     */
    capability_vocabulary: z.array(z.string()).default([]),
  }),
});
export type WireMeResponse = z.infer<typeof wireMeResponseSchema>;

export interface MeResult {
  user: AuthUser;
  /** The organization the request was scoped to, as the SERVER sees it today. */
  organization: Organization | null;
  /** Every organization the user is an ACTIVE member of, right now. */
  organizations: Organization[];
  capabilities: string[];
  capabilityVocabulary: string[];
}

/** Same normalization rules as login: an unknown type/role is `null`, never guessed. */
function normalizeMeOrganization(org: z.infer<typeof wireMeOrganizationSchema>): Organization {
  const parsedType = organizationTypeSchema.safeParse(org.type);
  const parsedRole = organizationRoleSchema.safeParse(org.role);
  return {
    id: org.id,
    name: org.name,
    domain: org.domain ?? '',
    type: parsedType.success ? parsedType.data : null,
    role: parsedRole.success ? parsedRole.data : null,
    salesControls: org.sales_controls,
  };
}

export function normalizeMeResponse(wire: WireMeResponse): MeResult {
  return {
    user: {
      id: wire.data.user.id,
      first_name: wire.data.user.first_name ?? '',
      last_name: wire.data.user.last_name ?? '',
      email: wire.data.user.email,
    },
    organization: wire.data.organization ? normalizeMeOrganization(wire.data.organization) : null,
    organizations: wire.data.organizations.map(normalizeMeOrganization),
    capabilities: wire.data.capabilities,
    capabilityVocabulary: wire.data.capability_vocabulary,
  };
}
